import { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Building2,
  MessageSquare,
  Trash2,
  Inbox,
  Search,
  Copy,
  Download,
  Circle,
  Check,
  Send,
  Sparkles,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getToken = () => localStorage.getItem("blancspace_admin_token");

const STATUS_META = {
  new: {
    label: "New",
    dot: "bg-brand-lime",
    text: "text-brand-lime",
    bg: "bg-brand-lime/10",
    border: "border-brand-lime/30",
  },
  contacted: {
    label: "Contacted",
    dot: "bg-brand-purple",
    text: "text-brand-purple",
    bg: "bg-brand-purple/10",
    border: "border-brand-purple/30",
  },
  closed: {
    label: "Closed",
    dot: "bg-zinc-500",
    text: "text-zinc-400",
    bg: "bg-zinc-500/10",
    border: "border-zinc-500/30",
  },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.new;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${meta.bg} ${meta.border} ${meta.text} font-mono text-[10px] uppercase tracking-wider`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [authOk, setAuthOk] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null); // {template_id, subject, html, name}
  const [sending, setSending] = useState(false);

  const adminEmail = localStorage.getItem("blancspace_admin_email");

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setAuthOk(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setAuthOk(true);
        fetchLeads();
        fetchTemplates();
      })
      .catch(() => {
        localStorage.removeItem("blancspace_admin_token");
        localStorage.removeItem("blancspace_admin_email");
        setAuthOk(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setLeads(data);
      if (data.length > 0) {
        setSelected((prev) => prev || data[0]);
      }
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reply-templates`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setTemplates(data);
    } catch (err) {
      // Templates are optional — don't toast
    }
  };

  const handlePreviewTemplate = async (templateId, templateName) => {
    if (!selected) return;
    try {
      const { data } = await axios.get(
        `${API}/admin/leads/${selected.id}/reply-preview/${templateId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setPreview({ ...data, name: templateName });
    } catch (err) {
      toast.error("Failed to load preview");
    }
  };

  const handleSendReply = async () => {
    if (!selected || !preview) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/leads/${selected.id}/reply`,
        { template_id: preview.template_id },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      // Auto-updated to contacted server-side
      const updated = { ...selected, status: "contacted" };
      setLeads((prev) =>
        prev.map((l) => (l.id === selected.id ? updated : l)),
      );
      setSelected(updated);
      setPreview(null);
      toast.success("Reply sent — lead marked Contacted");
    } catch (err) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("blancspace_admin_token");
    localStorage.removeItem("blancspace_admin_email");
    navigate("/admin/login", { replace: true });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const filtered = leads.filter((l) => l.id !== id);
      setLeads(filtered);
      if (selected?.id === id) setSelected(filtered[0] || null);
      toast.success("Lead deleted");
    } catch (err) {
      toast.error("Failed to delete lead");
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/leads/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Marked as ${STATUS_META[newStatus].label}`);
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/leads/export`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      link.setAttribute("download", `blancspace-leads-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error("Failed to export leads");
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const filtered = leads.filter((l) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      l.name.toLowerCase().includes(q) ||
      l.email.toLowerCase().includes(q) ||
      (l.company || "").toLowerCase().includes(q) ||
      l.message.toLowerCase().includes(q);
    const matchesStatus =
      statusFilter === "all" || (l.status || "new") === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = leads.reduce(
    (acc, l) => {
      const s = l.status || "new";
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    { new: 0, contacted: 0, closed: 0 },
  );

  if (authOk === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="font-mono text-sm text-zinc-500">// authenticating...</p>
      </div>
    );
  }
  if (authOk === false) return <Navigate to="/admin/login" replace />;

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-black text-white"
    >
      <div className="noise-overlay" />

      {/* Header */}
      <header className="glass border-b border-white/5 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between h-20 px-6 md:px-10">
          <a
            href="/"
            className="font-syne font-bold text-xl text-white tracking-tight"
          >
            BLANC<span className="text-brand-lime">SPACE</span>
            <span className="ml-3 font-mono text-xs text-zinc-500 tracking-widest uppercase">
              / admin
            </span>
          </a>

          <div className="flex items-center gap-4">
            <span className="hidden md:inline font-mono text-xs text-zinc-500">
              {adminEmail}
            </span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-full text-white font-mono text-xs uppercase tracking-wider hover:bg-white/10 transition-colors"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 relative">
        {/* Title */}
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="tech-label mb-3 inline-block">Inbox</span>
            <h1 className="font-syne font-bold text-4xl md:text-5xl text-white">
              Leads
              <span className="text-brand-lime ml-3">
                {loading ? "..." : leads.length}
              </span>
            </h1>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative w-full sm:w-64">
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search leads..."
                data-testid="admin-search-input"
                className="form-input pl-11"
              />
            </div>

            <button
              onClick={handleExportCSV}
              disabled={leads.length === 0}
              data-testid="admin-export-csv"
              className="flex items-center gap-2 px-5 py-3 border border-brand-lime/40 rounded-full text-brand-lime font-mono text-xs uppercase tracking-wider hover:bg-brand-lime/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Download size={14} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Status filter chips */}
        {!loading && leads.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            <FilterChip
              label={`All (${leads.length})`}
              active={statusFilter === "all"}
              onClick={() => setStatusFilter("all")}
              testId="filter-all"
            />
            <FilterChip
              label={`New (${statusCounts.new || 0})`}
              active={statusFilter === "new"}
              onClick={() => setStatusFilter("new")}
              accent="lime"
              testId="filter-new"
            />
            <FilterChip
              label={`Contacted (${statusCounts.contacted || 0})`}
              active={statusFilter === "contacted"}
              onClick={() => setStatusFilter("contacted")}
              accent="purple"
              testId="filter-contacted"
            />
            <FilterChip
              label={`Closed (${statusCounts.closed || 0})`}
              active={statusFilter === "closed"}
              onClick={() => setStatusFilter("closed")}
              accent="zinc"
              testId="filter-closed"
            />
          </div>
        )}

        {loading ? (
          <p className="font-mono text-sm text-zinc-500">// loading leads...</p>
        ) : leads.length === 0 ? (
          <div className="glass-card rounded-3xl p-16 text-center">
            <Inbox size={48} className="mx-auto text-zinc-600 mb-4" />
            <h3 className="font-syne font-bold text-2xl text-white mb-2">
              No leads yet
            </h3>
            <p className="font-mono text-sm text-zinc-500">
              // when someone submits the contact form, they'll show up here
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6">
            {/* Leads list */}
            <div className="glass-card rounded-3xl p-4 max-h-[75vh] overflow-y-auto">
              <div className="space-y-2">
                {filtered.map((lead) => (
                  <button
                    key={lead.id}
                    onClick={() => setSelected(lead)}
                    data-testid={`lead-item-${lead.id}`}
                    className={`w-full text-left p-4 rounded-2xl transition-all border ${
                      selected?.id === lead.id
                        ? "bg-brand-lime/10 border-brand-lime/40"
                        : "border-white/5 hover:border-white/20 hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1 gap-2">
                      <p className="font-syne font-bold text-white truncate">
                        {lead.name}
                      </p>
                      <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">
                        {formatDate(lead.created_at)}
                      </span>
                    </div>
                    <p className="font-mono text-xs text-zinc-400 truncate mb-2">
                      {lead.email}
                    </p>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-mono text-xs text-zinc-500 line-clamp-1 flex-1">
                        {lead.message}
                      </p>
                      <StatusBadge status={lead.status || "new"} />
                    </div>
                  </button>
                ))}
                {filtered.length === 0 && (
                  <p className="font-mono text-xs text-zinc-600 text-center py-8">
                    // no matches
                  </p>
                )}
              </div>
            </div>

            {/* Lead detail */}
            {selected && (
              <div
                data-testid="lead-detail"
                className="glass-card rounded-3xl p-8 md:p-10"
              >
                <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="tech-label">
                        {formatDate(selected.created_at)}
                      </span>
                      <StatusBadge status={selected.status || "new"} />
                    </div>
                    <h2 className="font-syne font-bold text-3xl md:text-4xl text-white">
                      {selected.name}
                    </h2>
                  </div>

                  <button
                    onClick={() => handleDelete(selected.id)}
                    data-testid="lead-delete-btn"
                    className="flex items-center gap-2 px-4 py-2 border border-red-500/30 rounded-full text-red-400 font-mono text-xs uppercase tracking-wider hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>

                {/* Status changer */}
                <div className="mb-8">
                  <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest mb-3">
                    Update Status
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {["new", "contacted", "closed"].map((s) => {
                      const meta = STATUS_META[s];
                      const isActive = (selected.status || "new") === s;
                      return (
                        <button
                          key={s}
                          onClick={() => handleStatusChange(selected.id, s)}
                          data-testid={`status-btn-${s}`}
                          className={`flex items-center gap-2 px-4 py-2 rounded-full border font-mono text-xs uppercase tracking-wider transition-all ${
                            isActive
                              ? `${meta.bg} ${meta.border} ${meta.text}`
                              : "border-white/10 text-zinc-500 hover:border-white/30 hover:text-white"
                          }`}
                        >
                          {isActive ? (
                            <Check size={12} />
                          ) : (
                            <Circle size={8} className={meta.dot} />
                          )}
                          {meta.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-6">
                  <DetailRow
                    icon={<Mail size={16} />}
                    label="Email"
                    value={selected.email}
                    onCopy={() => copyToClipboard(selected.email, "Email")}
                    href={`mailto:${selected.email}`}
                  />

                  {selected.company && (
                    <DetailRow
                      icon={<Building2 size={16} />}
                      label="Company"
                      value={selected.company}
                    />
                  )}

                  <div>
                    <div className="flex items-center gap-2 font-mono text-xs text-zinc-500 uppercase tracking-widest mb-3">
                      <MessageSquare size={16} className="text-brand-lime" />
                      Message
                    </div>
                    <div className="p-6 bg-white/5 rounded-2xl border border-white/5">
                      <p className="font-mono text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">
                        {selected.message}
                      </p>
                    </div>
                  </div>

                  {/* Quick Reply Templates */}
                  {templates.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 font-mono text-xs text-zinc-500 uppercase tracking-widest mb-3">
                        <Sparkles size={16} className="text-brand-lime" />
                        Quick Reply Templates
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {templates.map((t) => (
                          <button
                            key={t.id}
                            onClick={() => handlePreviewTemplate(t.id, t.name)}
                            data-testid={`reply-template-${t.id}`}
                            className="text-left p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-brand-lime/40 hover:bg-brand-lime/5 transition-all group"
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className="font-syne font-bold text-white text-sm">
                                {t.name}
                              </p>
                              <Send
                                size={14}
                                className="text-zinc-600 group-hover:text-brand-lime transition-colors flex-shrink-0 mt-0.5"
                              />
                            </div>
                            <p className="font-mono text-xs text-zinc-500 line-clamp-2">
                              {t.description}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-2">
                    <a
                      href={`mailto:${selected.email}?subject=Re: Your project inquiry with BlancSpace`}
                      className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 rounded-full text-white font-mono text-xs uppercase tracking-wider hover:bg-white/10 transition-colors"
                    >
                      <Mail size={14} />
                      Reply via my email client
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reply Preview Modal */}
      {preview && selected && (
        <div
          data-testid="reply-preview-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-sm"
          onClick={() => !sending && setPreview(null)}
        >
          <div
            className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden glass-card rounded-3xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-start justify-between gap-4 p-6 border-b border-white/5">
              <div>
                <span className="tech-label mb-2 inline-block">
                  {preview.name}
                </span>
                <p className="font-mono text-xs text-zinc-500">
                  // sending to{" "}
                  <span className="text-white">{selected.email}</span>
                </p>
              </div>
              <button
                onClick={() => !sending && setPreview(null)}
                className="text-zinc-500 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Preview body */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="mb-4">
                <p className="font-mono text-[10px] text-zinc-600 uppercase tracking-widest mb-1">
                  Subject
                </p>
                <p className="font-syne font-bold text-white text-lg">
                  {preview.subject}
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] text-zinc-600 uppercase tracking-widest mb-2">
                  Preview
                </p>
                <div
                  data-testid="reply-preview-html"
                  className="rounded-2xl overflow-hidden border border-white/5"
                  dangerouslySetInnerHTML={{ __html: preview.html }}
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-white/5">
              <button
                onClick={() => setPreview(null)}
                disabled={sending}
                className="px-5 py-3 border border-white/20 rounded-full text-white font-mono text-xs uppercase tracking-wider hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendReply}
                disabled={sending}
                data-testid="reply-send-btn"
                className="cta-button"
              >
                {sending ? "Sending..." : "Send Reply"}
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FilterChip = ({ label, active, onClick, accent, testId }) => {
  const activeStyles =
    accent === "lime"
      ? "bg-brand-lime/10 border-brand-lime/40 text-brand-lime"
      : accent === "purple"
        ? "bg-brand-purple/10 border-brand-purple/40 text-brand-purple"
        : accent === "zinc"
          ? "bg-zinc-500/10 border-zinc-500/40 text-zinc-300"
          : "bg-white/10 border-white/30 text-white";
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 rounded-full border font-mono text-xs uppercase tracking-wider transition-all ${
        active
          ? activeStyles
          : "border-white/10 text-zinc-500 hover:border-white/30 hover:text-white"
      }`}
    >
      {label}
    </button>
  );
};

const DetailRow = ({ icon, label, value, onCopy, href }) => (
  <div>
    <div className="flex items-center gap-2 font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">
      <span className="text-brand-lime">{icon}</span>
      {label}
    </div>
    <div className="flex items-center justify-between gap-4 p-4 bg-white/5 rounded-xl border border-white/5">
      {href ? (
        <a
          href={href}
          className="font-mono text-sm text-white hover:text-brand-lime transition-colors truncate"
        >
          {value}
        </a>
      ) : (
        <p className="font-mono text-sm text-white truncate">{value}</p>
      )}
      {onCopy && (
        <button
          onClick={onCopy}
          className="text-zinc-500 hover:text-brand-lime transition-colors flex-shrink-0"
          title="Copy"
        >
          <Copy size={14} />
        </button>
      )}
    </div>
  </div>
);
